/**
 * Multi-shop checkout — the pieces the cart, checkout and order screens share. Every component returns ``null``
 * for a single-shop cart / an ordinary order, so dropping one into a screen changes nothing for other tenants.
 */
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight, Layers, Store } from "lucide-react";
import { inr2 } from "../lib/api";
import { isMultiShopCart, shopGroups, shopIssues } from "../lib/multiShop";

/** One row per shop with what that shop's order will cost, and why a shop can't be ordered from right now. */
export function ShopBreakdown({ cart, className = "" }) {
    if (!isMultiShopCart(cart)) return null;
    const groups = shopGroups(cart);
    const issues = shopIssues(cart);
    return (
        <section className={`bg-card rounded-2xl border border-border p-4 ${className}`} data-testid="multi-shop-breakdown">
            <div className="flex items-start gap-2.5">
                <span className="w-9 h-9 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Layers size={18} /></span>
                <div className="min-w-0">
                    <h3 className="font-bold text-sm">Ordering from {groups.length} shops</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">One payment. Each shop prepares and delivers its own order, so you&apos;ll get {groups.length} separate orders to track.</p>
                </div>
            </div>
            <ul className="mt-3 divide-y divide-border">
                {groups.map((g) => {
                    const t = g.totals || {};
                    const count = (g.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
                    const issue = issues.find((i) => i.restaurant_id === g.restaurant_id);
                    return (
                        <li key={g.restaurant_id} className="py-2.5 first:pt-0 last:pb-0" data-testid={`multi-shop-row-${g.restaurant_id}`}>
                            <div className="flex items-baseline justify-between gap-3">
                                <p className="text-sm font-semibold min-w-0 truncate inline-flex items-center gap-1.5"><Store size={13} className="shrink-0 text-muted-foreground" />{g.restaurant_name}</p>
                                <p className="text-sm font-bold tabular-nums shrink-0">{inr2(t.grand_total)}</p>
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                {count} item{count === 1 ? "" : "s"} · items {inr2(t.subtotal)} · delivery {Number(t.delivery_fee) > 0 ? inr2(t.delivery_fee) : "FREE"}
                                {Number(t.discount) > 0 ? ` · saved ${inr2(t.discount)}` : ""}
                            </p>
                            {issue && (
                                <p className="mt-1.5 text-xs font-semibold text-red-700 dark:text-red-300 inline-flex items-start gap-1.5" data-testid={`multi-shop-issue-${g.restaurant_id}`}>
                                    <AlertTriangle size={13} className="shrink-0 mt-0.5" /> <span>{issue.message} Remove its items to continue.</span>
                                </p>
                            )}
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

/** A shop heading inside a flat item list: rendered before the first item of each shop (``as="li"`` inside a list). */
export function ShopDivider({ cart, items, index, as: Tag = "p" }) {
    if (!isMultiShopCart(cart)) return null;
    const it = items[index];
    if (!it || (index > 0 && items[index - 1].restaurant_id === it.restaurant_id)) return null;
    return (
        <Tag className={`${index > 0 ? "pt-4" : "pt-1"} pb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 list-none`}
             data-testid={`multi-shop-heading-${it.restaurant_id}`}>
            <Store size={12} /> {it.restaurant_name}
        </Tag>
    );
}

/** On an order that came from a multi-shop checkout: which one of how many. ``link`` makes it a link to them all. */
export function OrderGroupChip({ order, link = false, className = "" }) {
    if (!order?.order_group_id) return null;
    const text = `Order ${order.order_group_seq || 1} of ${order.order_group_size || 1} from one checkout`;
    const cls = `inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 bg-primary/10 text-primary ${className}`;
    if (!link) return <span className={cls} data-testid={`order-group-chip-${order.id}`}><Layers size={11} /> {text}</span>;
    return (
        <Link to={`/order-groups/${order.order_group_id}`} className={cls} data-testid={`order-group-link-${order.id}`}>
            <Layers size={11} /> {text} · see all <ChevronRight size={12} />
        </Link>
    );
}
